import React, { useState } from 'react';
import { Card } from '../../../design-system/Card';
import { Button } from '../../../design-system/Button';
import { Badge } from '../../../design-system/Badge';
import { Tabs } from '../../../design-system/Tabs';
import {
  Palette,
  Layout,
  Image,
  Download
} from 'lucide-react';

export const CreativeView = () => {
  const [activeTab, setActiveTab] = useState('moodboard');

  const tabs = [
    { id: 'moodboard', label: 'Moodboards & Color Palettes', icon: <Palette size={15} /> },
    { id: 'concepts', label: 'Concept Iterations & Reviews', icon: <Layout size={15} /> },
    { id: 'assets', label: 'Brand Assets & Deliverables', icon: <Image size={15} /> }
  ];

  const colorPalette = [
    { name: 'Neomorphic Primary Teal', hex: '#006A63', role: 'Brand & CTA Accent' },
    { name: 'Luminous Cyan Highlight', hex: '#43DDD0', role: 'Dark Mode Focus Glow' },
    { name: 'Obsidian Charcoal Base', hex: '#111417', role: 'Dark Theme Surface' },
    { name: 'Soft Gray Canvas', hex: '#F6FAFD', role: 'Light Theme Surface' },
    { name: 'Electric Purple Accent', hex: '#7C4DFF', role: 'Specialized Tag' }
  ];

  const conceptIterations = [
    {
      id: 'V3',
      title: 'Tactile Dual-Shadow Elevation Spec (Final)',
      designer: 'Rohith Prince',
      status: 'Client Approved',
      feedback: 'Soft lighting offsets give realistic tactile depth without clutter.',
      date: 'Aug 22, 2026'
    },
    {
      id: 'V2',
      title: 'Glassmorphic Semi-Transparent Borders',
      designer: 'Elena Rostov',
      status: 'Superseded',
      feedback: 'High GPU blur overhead on low-end mobile devices.',
      date: 'Aug 14, 2026'
    },
    {
      id: 'V1',
      title: 'Flat Minimalist Monochrome',
      designer: 'Marcus Vance',
      status: 'Superseded',
      feedback: 'Lacked visual hierarchy between nested containers.',
      date: 'Aug 02, 2026'
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} className="animate-fade-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Badge variant="purple" size="sm">Creative & Design Methodology</Badge>
            <h2 style={{ fontSize: '20px', fontWeight: '800' }}>Creative Studio & Brand Suite</h2>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            Moodboard inspiration, color tokens, typography systems, and multi-version client reviews
          </p>
        </div>

        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* TAB 1: MOODBOARD & COLORS */}
      {activeTab === 'moodboard' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Color Tokens Matrix */}
          <Card padding="24px">
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '16px' }}>Design System Color Palette</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px' }}>
              {colorPalette.map((col) => (
                <div key={col.hex} style={{ padding: '12px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ height: '56px', backgroundColor: col.hex, borderRadius: 'var(--radius-sm)', marginBottom: '8px', boxShadow: 'var(--shadow-raised-sm)' }} />
                  <div style={{ fontSize: '13px', fontWeight: '700' }}>{col.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--accent-teal)', fontFamily: 'monospace', fontWeight: '700' }}>{col.hex}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{col.role}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Typography Specimen */}
          <Card padding="24px">
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '12px' }}>Typography System</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ padding: '12px 16px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>PRIMARY HEADINGS — Plus Jakarta Sans 800</div>
                <div style={{ fontSize: '20px', fontWeight: '800', marginTop: '2px' }}>Modularity Without Compromise</div>
              </div>
              <div style={{ padding: '12px 16px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>CODE & DATA IDENTIFIERS — JetBrains Mono</div>
                <div style={{ fontSize: '14px', fontFamily: 'monospace', color: 'var(--accent-teal)', marginTop: '2px' }}>
                  FOR-101 • 3.4 days cycle time • 95% confidence
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* TAB 2: CONCEPTS & REVIEWS */}
      {activeTab === 'concepts' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {conceptIterations.map((concept) => (
            <Card key={concept.id} padding="22px">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Badge variant="purple" size="sm">Iteration {concept.id}</Badge>
                  <h4 style={{ fontSize: '16px', fontWeight: '800' }}>{concept.title}</h4>
                </div>
                <Badge variant={concept.status === 'Client Approved' ? 'success' : 'neutral'} size="xs">
                  {concept.status}
                </Badge>
              </div>

              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', marginBottom: '10px' }}>
                <strong>Stakeholder Review Notes:</strong> {concept.feedback}
              </p>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)', paddingTop: '8px', borderTop: '1px solid var(--border-divider)' }}>
                <span>Lead Designer: {concept.designer}</span>
                <span>Logged {concept.date}</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* TAB 3: BRAND ASSETS */}
      {activeTab === 'assets' && (
        <Card padding="24px">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800' }}>Production Asset Deliverables</h3>
            <Button variant="primary" size="sm" icon={<Download size={14} />}>Download All Assets (ZIP)</Button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
            {[
              { name: 'Forma Brandmark Vector', format: 'SVG (Vector)', size: '24 KB' },
              { name: 'Neomorphic Dual Theme Spec', format: 'PDF Document', size: '1.4 MB' },
              { name: 'App Icon Pack (16px - 512px)', format: 'PNG Asset Bundle', size: '3.8 MB' }
            ].map((a, i) => (
              <div key={i} style={{ padding: '14px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700' }}>{a.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{a.format} • {a.size}</div>
                </div>
                <Button variant="ghost" size="xs" icon={<Download size={13} />} />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
